import { FormControl, Grid, InputAdornment, InputLabel } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useLocale } from '@repo/i18n';
import { PhoneNumber } from '@repo/types';
import TextFieldStyleUtils from 'src/components/utils/jss/TextFieldStyleUtils';
import { capitalize } from '@repo/common-utils/TextUtils';

import type { JSX } from 'react';

// Static IDs and error props for form fields (singleton component - no dynamic IDs needed)
const FIELD = {
    dialCode: {
        id: 'phone-dialCode',
        errorProps: { id: 'phone-dialCode-error', role: 'alert' as const },
    },
    number: {
        id: 'phone-number',
        errorProps: { id: 'phone-number-error', role: 'alert' as const },
    },
} as const;

interface Props {
    phoneNumber: PhoneNumber;
    onChange: (phoneNumber: PhoneNumber) => void;
    errorPrefix: boolean;
    errorNumber: boolean;
}
export default function PhoneInput(props: Props): JSX.Element {
    const { t } = useLocale();

    return (
        <FormControl>
            <InputLabel
                htmlFor={FIELD.number.id}
                error={props.errorPrefix || props.errorNumber}
                required={true}
                sx={TextFieldStyleUtils.inputLabelAboveFieldSx}
            >
                {capitalize(t.phone_number)}
            </InputLabel>
            <Grid container spacing={2}>
                <Grid size={3}>
                    <TextField
                        id={FIELD.dialCode.id}
                        error={props.errorPrefix}
                        aria-invalid={props.errorPrefix || undefined}
                        aria-describedby={
                            props.errorPrefix ? FIELD.dialCode.errorProps.id : undefined
                        }
                        required={true}
                        value={props.phoneNumber.dialCode}
                        onChange={(e) =>
                            props.onChange({
                                ...props.phoneNumber,
                                dialCode: e.currentTarget.value,
                            })
                        }
                        aria-label={capitalize(t.dial_code)}
                        variant="outlined"
                        type={'text'}
                        color="secondary"
                        margin="dense"
                        size="small"
                        name="phoneDialCode"
                        autoComplete="tel-country-code"
                        helperText={
                            props.errorPrefix
                                ? `${capitalize(t.dial_code)} ${t.is_required}`
                                : undefined
                        }
                        FormHelperTextProps={
                            props.errorPrefix ? FIELD.dialCode.errorProps : undefined
                        }
                        inputProps={{
                            sx: {
                                paddingLeft: '0px !important',
                            },
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+</InputAdornment>,
                        }}
                    />
                </Grid>
                <Grid size={9}>
                    <TextField
                        id={FIELD.number.id}
                        sx={{
                            width: '100%',
                            paddingLeft: 0,
                        }}
                        autoComplete="tel-national"
                        error={props.errorNumber}
                        aria-invalid={props.errorNumber || undefined}
                        aria-describedby={
                            props.errorNumber ? FIELD.number.errorProps.id : undefined
                        }
                        required={true}
                        value={props.phoneNumber.number}
                        onChange={(e) =>
                            props.onChange({ ...props.phoneNumber, number: e.currentTarget.value })
                        }
                        variant="outlined"
                        type={'text'}
                        color="secondary"
                        margin="dense"
                        size="small"
                        name="phoneNumber"
                        helperText={
                            props.errorNumber
                                ? `${capitalize(t.phone_number)} ${t.is_required}`
                                : undefined
                        }
                        FormHelperTextProps={
                            props.errorNumber ? FIELD.number.errorProps : undefined
                        }
                        inputProps={{
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                        }}
                    />
                </Grid>
            </Grid>
        </FormControl>
    );
}
